import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { sendChatMessage } from "../../api/api";

const AIChat = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      sender: "assistant",
      text: "Hello! I'm your journal assistant. How are you feeling today?",
    },
  ]);
  const [userMessage, setUserMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (userMessage.trim()) {
      const newUserMessage = userMessage;
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "user", text: newUserMessage },
      ]);
      setUserMessage("");
      setIsLoading(true);

      try {
        const assistantResponse = await sendChatMessage(newUserMessage);
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: "assistant", text: assistantResponse },
        ]);
      } catch (error) {
        console.error("Error sending message to chatbot:", error);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "assistant",
            text: "Sorry, I couldn't get a response right now.",
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 p-4">
        <h3 className="text-lg font-semibold flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-purple-600" />
          Journal Assistant
        </h3>
        <button
          type="button"
          className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`max-w-[75%] ${
              message.sender === "user" ? "ml-auto" : ""
            }`}
          >
            <div
              className={`rounded-2xl p-3 ${
                message.sender === "user"
                  ? "rounded-tr-md bg-gray-100"
                  : "rounded-tl-md bg-purple-50 text-purple-950"
              }`}
            >
              <p className="text-sm">{message.text}</p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-center">
            <div className="loader"></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isLoading
                ? "Waiting for response..."
                : "Message your assistant..."
            }
            className="flex-1 rounded-full border border-gray-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#9B30FF]"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={handleSend}
            className="rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] p-2 text-white transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-purple-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!userMessage.trim() || isLoading}
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
